/* Creskale theme behavior. Prices and totals always come from Shopify: after a change the theme asks
   Shopify to render the affected section again instead of formatting money in the browser. */
(() => {
  const config = window.creskale || { routes: {}, strings: {} };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* Quantity steppers and sort menus */

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-step]');
    if (!step) return;
    const input = step.closest('[data-stepper]')?.querySelector('input');
    if (!input) return;
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) event.target.form.submit();
  });

  /* Sliders */

  function initSlider(root) {
    const track = root.querySelector('[data-slider-track]');
    if (!track || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const prev = root.querySelector('[data-slider-prev]');
    const next = root.querySelector('[data-slider-next]');
    const update = () => {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * 0.9, behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * 0.9, behavior: 'smooth' }));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    update();
  }

  function initSlideshow(root) {
    const track = root.querySelector('[data-slider-track]');
    if (!track || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const slides = [...track.children];
    const dots = [...root.querySelectorAll('[data-slide-to]')];
    let index = 0;
    let timer = null;
    const go = (target) => {
      index = (target + slides.length) % slides.length;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
    };
    dots.forEach((dot) => dot.addEventListener('click', () => go(Number(dot.dataset.slideTo))));
    track.addEventListener(
      'scroll',
      () => {
        index = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === index)));
      },
      { passive: true },
    );
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (root.dataset.autoplay !== 'true' || slides.length < 2 || reduced) return;
    const start = () => {
      clearInterval(timer);
      timer = setInterval(() => go(index + 1), Number(root.dataset.speed || 6) * 1000);
    };
    const stop = () => clearInterval(timer);
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    start();
  }

  /* Cart */

  // The browser's fetch, for reading cart sections; other scripts' cart requests are watched below.
  const request = window.fetch.bind(window);
  // The theme adds and changes lines through window.fetch as it is when the buyer clicks, so apps
  // that extend cart requests, such as Kaching Bundles adding its upsells, see the theme's too.
  const ownRequests = new WeakSet();
  const cartFetch = (url, init) => {
    ownRequests.add(init);
    return window.fetch(url, init);
  };
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let lastFocus = null;

  function openCart() {
    const panel = drawer();
    if (!panel) return;
    lastFocus = document.activeElement;
    panel.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    panel.querySelector('[data-cart-content]')?.focus();
  }

  function closeCart() {
    const panel = drawer();
    if (!panel || panel.dataset.open !== 'true') return;
    panel.dataset.open = 'false';
    document.body.style.overflow = '';
    lastFocus?.focus?.();
  }

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = Number(count) === 0;
    });
  }

  function cartSections() {
    const ids = [];
    if (drawer()) ids.push('cart-drawer');
    const page = document.querySelector('[data-cart-page]');
    if (page) ids.push(page.dataset.section);
    return ids;
  }

  /**
   * Brings the shown cart up to date in place: only the lines and totals that changed are swapped,
   * so the other lines, their photos and the scroll position stay as they are. A cart that becomes
   * empty, or stops being empty, is replaced whole.
   */
  function patchCart(current, fresh) {
    const lines = current.querySelector('.cs-cart-items');
    const freshLines = fresh.querySelector('.cs-cart-items');
    if (!lines || !freshLines) {
      current.replaceWith(fresh);
      return;
    }
    const shown = new Map([...lines.children].map((line) => [line.dataset.cartLine, line]));
    let previous = null;
    [...freshLines.children].forEach((next) => {
      let line = shown.get(next.dataset.cartLine);
      shown.delete(next.dataset.cartLine);
      if (!line || !line.isEqualNode(next)) {
        line?.replaceWith(next);
        line = next;
      }
      const place = previous ? previous.nextElementSibling : lines.firstElementChild;
      if (line !== place) lines.insertBefore(line, place);
      previous = line;
    });
    shown.forEach((line) => line.remove());
    current.querySelectorAll('[data-cart-part]').forEach((part) => {
      const next = fresh.querySelector(`[data-cart-part="${part.dataset.cartPart}"]`);
      if (next && !part.isEqualNode(next)) part.replaceWith(next);
    });
    current.dataset.cartCountValue = fresh.dataset.cartCountValue;
  }

  function renderCart(sections) {
    Object.entries(sections || {}).forEach(([id, html]) => {
      if (!html) return;
      const fresh = parse(html).querySelector('[data-cart-content]');
      if (!fresh) return;
      const current =
        id === 'cart-drawer'
          ? drawer()?.querySelector('[data-cart-content]')
          : document.querySelector(`[data-cart-page][data-section="${id}"] [data-cart-content]`);
      if (current) patchCart(current, fresh);
      updateCount(fresh.dataset.cartCountValue);
    });
    syncGift();
  }

  /** Reads the cart sections again after a change made outside the theme's own buttons. */
  async function refreshCart(open) {
    const ids = cartSections();
    if (!ids.length) return;
    try {
      const response = await request(`${window.location.pathname}?sections=${ids.join(',')}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      renderCart(await response.json());
      if (open && config.cartType === 'drawer') openCart();
    } catch (error) {
      // The cart shows the change on the next page load.
    }
  }

  // Apps that add to the cart themselves, such as bundle apps, get the same cart drawer as the
  // theme's own button: the theme notices their cart requests, then shows the updated cart.
  const CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const isAdd = (target) => /\/cart\/add/.test(target);
  window.fetch = async (...args) => {
    const response = await request(...args);
    const target = args[0] instanceof Request ? args[0].url : String(args[0]);
    const own = args[1] && ownRequests.has(args[1]);
    if (response.ok && !own && CART_REQUEST.test(target)) refreshCart(isAdd(target));
    return response;
  };
  const openRequest = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, target, ...rest) {
    const url = String(target);
    if (CART_REQUEST.test(url)) this.addEventListener('load', () => this.status < 300 && refreshCart(isAdd(url)));
    return openRequest.call(this, method, target, ...rest);
  };
  ['cart:refresh', 'cart:updated'].forEach((name) => document.addEventListener(name, () => refreshCart(false)));
  window.creskale = Object.assign(config, { refreshCart, openCart });

  function cartError(message) {
    document.querySelectorAll('[data-cart-content]').forEach((content) => {
      content.removeAttribute('aria-busy');
      let notice = content.querySelector('[data-cart-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.className = 'cs-notice cs-notice--error';
        notice.setAttribute('role', 'alert');
        notice.dataset.cartError = '';
        content.prepend(notice);
      }
      notice.textContent = message || config.strings.cartError;
    });
  }

  /** Changes one line, found by its line item key; only that line shows it is updating. */
  async function changeLine(key, quantity) {
    document.querySelectorAll('[data-cart-line]').forEach((line) => {
      if (line.dataset.cartLine === key) line.setAttribute('aria-busy', 'true');
    });
    document.querySelectorAll('.cs-cart').forEach((cart) => cart.setAttribute('aria-busy', 'true'));
    try {
      const response = await cartFetch(config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: cartSections(), sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message);
      if (data.sections) renderCart(data.sections);
      else await refreshCart(false);
    } catch (error) {
      cartError(error.message);
    } finally {
      document.querySelectorAll('.cs-cart[aria-busy], [data-cart-line][aria-busy]').forEach((element) => {
        element.removeAttribute('aria-busy');
      });
    }
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && config.cartType === 'drawer' && drawer()) {
      event.preventDefault();
      openCart();
      return;
    }
    if (event.target.closest('[data-cart-close]')) closeCart();
    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.dataset.lineRemove, 0);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCart();
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-quantity]');
    if (!input) return;
    changeLine(input.dataset.lineQuantity, Math.max(0, Math.floor(Number(input.value) || 0)));
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    // An app in the form, such as Kaching Bundles, may add to the cart itself; the cart drawer
    // still opens, through the cart request watcher above.
    if (!form || event.defaultPrevented || config.cartType !== 'drawer' || !drawer()) return;
    event.preventDefault();
    const error = form.querySelector('[data-product-error]');
    // Read as an attribute: the form's id property is its hidden input named "id".
    const id = form.getAttribute('id');
    const buttons = document.querySelectorAll(`#${id} [type="submit"], [form="${id}"][type="submit"]`);
    buttons.forEach((button) => button.setAttribute('aria-disabled', 'true'));
    if (error) error.hidden = true;
    const body = new FormData(form);
    body.append('sections', cartSections().join(','));
    body.append('sections_url', window.location.pathname);
    try {
      const response = await cartFetch(config.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (data.status) {
        if (error) {
          error.textContent = data.description || data.message || config.strings.cartError;
          error.hidden = false;
        }
        return;
      }
      // An app that rewrote the request may have left out the sections; read them again then.
      if (data.sections) {
        renderCart(data.sections);
        openCart();
      } else refreshCart(true);
    } catch (failure) {
      if (error) {
        error.textContent = config.strings.cartError;
        error.hidden = false;
      }
    } finally {
      buttons.forEach((button) => button.removeAttribute('aria-disabled'));
    }
  });

  /* Test mode: in the theme editor the cart drawer can stay open, so it can be styled while looking at it. */
  function keepDrawerOpenInEditor(target) {
    if (!window.Shopify || !window.Shopify.designMode) return;
    const panel = drawer();
    if (!panel) return;
    if (panel.dataset.testMode === 'true') openCart();
    else if (target) document.body.style.overflow = '';
  }
  document.addEventListener('DOMContentLoaded', () => keepDrawerOpenInEditor(null));
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-cart-drawer]')) keepDrawerOpenInEditor(event.target);
  });

  /* Cart blocks: upsells, free gift, note, discount code, policies and the countdown */

  /** Adds one variant to the cart and shows the updated cart. */
  async function addVariant(id, properties) {
    const response = await cartFetch(config.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1, properties }],
        sections: cartSections(),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message);
    if (data.sections) renderCart(data.sections);
    else await refreshCart(false);
  }

  document.addEventListener('click', async (event) => {
    const add = event.target.closest('[data-upsell-add]');
    if (add) {
      const card = add.closest('[data-upsell]');
      const id = card?.querySelector('[data-upsell-variant]')?.value || add.dataset.upsellAdd;
      add.setAttribute('aria-disabled', 'true');
      try {
        await addVariant(id);
      } catch (error) {
        cartError(error.message);
      } finally {
        add.removeAttribute('aria-disabled');
      }
      return;
    }
    // The policies tick must be on before checking out.
    const checkout = event.target.closest('[name="checkout"]');
    const drawerPanel = checkout?.closest('[data-cart-drawer]');
    if (!drawerPanel) return;
    const unticked = [...drawerPanel.querySelectorAll('[data-cart-policy]')].filter((box) => !box.checked);
    if (!unticked.length) return;
    event.preventDefault();
    drawerPanel.querySelectorAll('[data-cart-policy-error]').forEach((message) => {
      message.hidden = false;
    });
    unticked[0].focus();
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-policy]')) return;
    event.target.closest('[data-cart-content]')?.querySelectorAll('[data-cart-policy-error]').forEach((message) => {
      message.hidden = event.target.checked || message.hidden;
    });
  });

  // The order note is saved with the cart, a moment after typing stops.
  let noteTimer = null;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      cartFetch(config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value }),
      }).catch(() => cartError());
    }, 600);
  });

  // A discount code is kept for checkout, where Shopify checks it and takes the discount off.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-cart-discount]');
    if (!form) return;
    event.preventDefault();
    const code = form.elements.code.value.trim();
    if (!code) return;
    const status = form.querySelector('[data-cart-discount-status]');
    try {
      await request(`/discount/${encodeURIComponent(code)}`, { headers: { Accept: 'text/html' } });
      status.textContent = config.strings.discountSaved.replace('__CODE__', code);
    } catch (error) {
      status.textContent = config.strings.cartError;
    }
    status.hidden = false;
  });

  // The free gift follows the cart: added when the amount is reached, removed when it is not.
  let giftBusy = false;
  async function syncGift() {
    const gift = document.querySelector('[data-cart-gift]');
    if (!gift || giftBusy) return;
    const qualified = gift.dataset.giftQualified === 'true';
    const key = gift.dataset.giftKey;
    if (qualified === Boolean(key)) return;
    const failed = `creskale-gift-failed-${gift.dataset.giftVariant}`;
    try {
      // A gift that could not be added once is not tried again in this visit.
      if (qualified && sessionStorage.getItem(failed)) return;
    } catch (error) {
      // Storage may be blocked; the gift is still tried.
    }
    giftBusy = true;
    try {
      if (qualified) await addVariant(gift.dataset.giftVariant, { _free_gift: 'true' });
      else await changeLine(key, 0);
    } catch (error) {
      try {
        sessionStorage.setItem(failed, 'true');
      } catch (storageError) {
        // Nothing to remember it with.
      }
    } finally {
      giftBusy = false;
    }
  }
  syncGift();

  // Countdown timers count to the end date the store set, the same for everyone.
  function tickCountdowns() {
    document.querySelectorAll('[data-countdown-end]').forEach((countdown) => {
      const left = Number(countdown.dataset.countdownEnd) - Math.floor(Date.now() / 1000);
      if (left <= 0) {
        countdown.hidden = true;
        return;
      }
      const days = Math.floor(left / 86400);
      const pad = (value) => String(value).padStart(2, '0');
      const clock = `${pad(Math.floor((left % 86400) / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
      const timer = countdown.querySelector('[data-countdown-timer]');
      if (timer) timer.textContent = days > 0 ? `${days}d ${clock}` : clock;
    });
  }
  tickCountdowns();
  setInterval(tickCountdowns, 1000);

  /* Product */

  function showMedia(root, mediaId) {
    const track = root.querySelector('[data-gallery-track]');
    const slide = root.querySelector(`[data-media-id="${mediaId}"]`);
    if (track && slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  function initGallery(root) {
    const track = root.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = [...track.children];
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showMedia(root, thumb.dataset.galleryThumb)));
    // The arrows move one photo at a time and loop from the last photo back to the first.
    const current = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (index) => {
      const slide = slides[(index + slides.length) % slides.length];
      if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    };
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(current() - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(current() + 1));
    let pending = null;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(pending);
        pending = requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / track.clientWidth);
          thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', String(n === index)));
          slides.forEach((slide, n) => {
            if (n !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
          });
        });
      },
      { passive: true },
    );
  }

  function initStickyBar(root) {
    const bar = root.querySelector('[data-sticky-atc]');
    const main = root.querySelector('[data-add-button]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.dataset.visible = String(show);
      bar.inert = !show;
    }).observe(main);
  }

  function initVariants(root) {
    const picker = root.querySelector('[data-variant-picker]');
    const data = root.querySelector('[data-product-json]');
    if (!picker || !data) return;
    const product = JSON.parse(data.textContent);
    picker.addEventListener('change', async () => {
      const selected = [...picker.querySelectorAll('fieldset')].map((fieldset) => {
        const choice = fieldset.querySelector('input:checked') || fieldset.querySelector('select');
        return choice ? choice.value : null;
      });
      selected.forEach((value, index) => {
        const label = picker.querySelector(`[data-option-value="${index}"]`);
        if (label) label.textContent = value;
      });
      const variant = product.variants.find((candidate) => candidate.options.every((value, i) => value === selected[i]));
      const buttons = root.querySelectorAll('[data-add-button], [data-sticky-atc] [type="submit"]');
      if (!variant) {
        buttons.forEach((button) => {
          button.disabled = true;
          button.textContent = config.strings.unavailable;
        });
        return;
      }
      root.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      if (variant.featured_media) showMedia(root, variant.featured_media.id);

      // A product page renders just this section; elsewhere, such as the home page, the section is
      // not part of the product's template, so the product page is read instead.
      const source =
        root.dataset.template === 'product'
          ? `${root.dataset.url}?variant=${variant.id}&section_id=${root.dataset.section}`
          : `${root.dataset.url}?variant=${variant.id}`;
      const response = await fetch(source);
      const page = parse(await response.text());
      const fresh = page.querySelector('[data-cs-product]') || page;
      const quantity = root.querySelector('[data-update="quantity"] input:checked')?.value;
      root.querySelectorAll('[data-update]').forEach((node) => {
        const next = fresh.querySelector(`[data-update="${node.dataset.update}"]`);
        if (next) node.innerHTML = next.innerHTML;
      });
      if (quantity) {
        const radio = root.querySelector(`[data-update="quantity"] input[value="${quantity}"]`);
        if (radio) radio.checked = true;
      }
    });
  }

  function initProduct(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    initGallery(root);
    initStickyBar(root);
    initVariants(root);
  }

  async function loadRecommendations(root) {
    if (root.dataset.ready || root.querySelector('.cs-grid')) return;
    root.dataset.ready = 'true';
    try {
      const response = await fetch(root.dataset.url);
      const fresh = parse(await response.text()).querySelector('[data-recommendations]');
      if (fresh && fresh.querySelector('.cs-grid')) root.innerHTML = fresh.innerHTML;
    } catch (error) {
      root.hidden = true;
    }
  }

  /* Video showcase: the videos on screen play muted and on a loop; a tap pauses or plays one, and
     that choice holds while the buyer scrolls. With reduced motion nothing plays on its own. */
  function initShowcase(root) {
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.querySelectorAll('.cs-showcase__card').forEach((card) => {
      const video = card.querySelector('video');
      const toggle = card.querySelector('[data-showcase-toggle]');
      if (!video || !toggle) return;
      video.muted = true;
      let paused = false;
      const show = () => {
        card.dataset.playing = String(!video.paused);
        toggle.setAttribute('aria-label', video.paused ? config.strings.playVideo : config.strings.pauseVideo);
      };
      video.addEventListener('play', show);
      video.addEventListener('pause', show);
      toggle.addEventListener('click', () => {
        paused = !video.paused;
        if (paused) video.pause();
        else video.play().catch(() => {});
      });
      if (!still && 'IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting) video.pause();
            else if (!paused) video.play().catch(() => {});
          },
          { threshold: 0.5 },
        ).observe(card);
      }
      show();
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-showcase]').forEach(initShowcase);
    scope.querySelectorAll('[data-slideshow]').forEach(initSlideshow);
    scope.querySelectorAll('[data-cs-product]').forEach(initProduct);
    scope.querySelectorAll('[data-recommendations]').forEach(loadRecommendations);
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /* Sticky header. With data-reveal, it slides away while the shopper scrolls down and comes back as
     soon as they scroll up; it stays while the phone menu is open and near the top of the page.
     --cs-header-offset is the height it covers, so the product photos stay clear of it. */
  let lastScroll = window.scrollY;
  let headerFrame = 0;

  function updateHeader() {
    headerFrame = 0;
    const header = document.querySelector('[data-sticky-header]');
    const y = Math.max(window.scrollY, 0);
    if (!header) {
      document.documentElement.style.removeProperty('--cs-header-offset');
      lastScroll = y;
      return;
    }
    const height = header.offsetHeight;
    let hidden = false;
    if (header.dataset.reveal === 'true' && y > height && !header.querySelector('details[open]')) {
      const delta = y - lastScroll;
      if (Math.abs(delta) < 6) return;
      hidden = delta > 0;
    }
    lastScroll = y;
    header.dataset.hidden = String(hidden);
    document.documentElement.style.setProperty('--cs-header-offset', hidden ? '0px' : `${height}px`);
  }

  const queueHeader = () => {
    if (!headerFrame) headerFrame = window.requestAnimationFrame(updateHeader);
  };
  window.addEventListener('scroll', queueHeader, { passive: true });
  window.addEventListener('resize', queueHeader);
  document.addEventListener('shopify:section:load', queueHeader);
  updateHeader();

  // In the theme editor, the cart drawer opens while it or one of its blocks is selected, and stays
  // open when the editor redraws it after a change.
  let editingCart = false;
  const editsCart = (event) =>
    Boolean(event.target.closest?.('[data-cart-drawer]') || event.target.querySelector?.('[data-cart-drawer]'));
  document.addEventListener('shopify:section:select', (event) => {
    editingCart = editsCart(event);
    if (editingCart) openCart();
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (!editsCart(event)) return;
    editingCart = true;
    openCart();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (!editsCart(event)) return;
    editingCart = false;
    closeCart();
  });
  document.addEventListener('shopify:section:load', (event) => {
    if (editingCart && editsCart(event)) openCart();
  });
})();
